import { BrowserRouter, Navigate, Outlet, Routes, Route } from 'react-router-dom';
import './App.css';
import { DemoSessionProvider, useDemoSession } from './auth/DemoSessionProvider';
import { isDemoAuth, isJwtOnly } from './auth/authMode';
import { workspaceHomePath } from './auth/workspaces';
import { AppLayout } from './layouts/AppLayout';
import { DemoSignInPage } from './pages/auth/DemoSignInPage';
import { EntraCallbackPage } from './pages/auth/EntraCallbackPage';
import { SignInPage } from './pages/auth/SignInPage';
import { WorkspacePickerPage } from './pages/auth/WorkspacePickerPage';
import { AcceptInvitePage } from './pages/auth/AcceptInvitePage';
import { AdminOrganizationsPage } from './pages/admin/AdminOrganizationsPage';
import { AdminConnectionsPage } from './pages/admin/AdminConnectionsPage';
import { OrganizationsPage } from './pages/provider/admin/OrganizationsPage';
import { OrganizationCreatePage } from './pages/provider/admin/OrganizationCreatePage';
import { OrganizationDetailPage } from './pages/provider/admin/OrganizationDetailPage';
import { ProgramsPage } from './pages/provider/admin/ProgramsPage';
import { ProgramCreatePage } from './pages/provider/admin/ProgramCreatePage';
import { ConnectionsPage } from './pages/provider/admin/ConnectionsPage';
import { PeoplePage } from './pages/provider/admin/PeoplePage';
import { RolesPage } from './pages/provider/admin/RolesPage';
import { DigitizeBindingsPage } from './pages/provider/admin/DigitizeBindingsPage';
import { AdminAuditPage } from './pages/provider/admin/AdminAuditPage';
import { FEATURES } from './config/features';
import { OrgMembersPage } from './pages/admin/OrgMembersPage';
import { OrgInvitationsPage } from './pages/admin/OrgInvitationsPage';
import { DashboardPage } from './pages/originator/DashboardPage';
import { NewSubmissionPage } from './pages/originator/NewSubmissionPage';
import { PackagesPage } from './pages/originator/PackagesPage';
import { PackageDetailPage } from './pages/originator/PackageDetailPage';
import { ExceptionsPage } from './pages/originator/ExceptionsPage';
import { ExceptionDetailPage } from './pages/originator/ExceptionDetailPage';
import { PortfolioExplorerPage } from './pages/originator/PortfolioExplorerPage';
import { LoanDetailPage } from './pages/originator/LoanDetailPage';
import { RequestsPage } from './pages/originator/RequestsPage';
import { SearchAskPage } from './pages/originator/SearchAskPage';
import { ReportsHubPage } from './pages/originator/ReportsHubPage';
import { OriginatorReportDashboardPage } from './pages/originator/OriginatorReportDashboardPage';
import { ReportDashboardPage } from './pages/reports/ReportDashboardPage';
import { ProviderDashboardPage } from './pages/provider/ProviderDashboardPage';
import { IntakeExplorerBatchPage } from './pages/provider/IntakeExplorerBatchPage';
import { IntakeExplorerProgramsPage } from './pages/provider/IntakeExplorerProgramsPage';
import { AuthorityReviewQueuePage } from './pages/provider/AuthorityReviewQueuePage';
import { CandidateReviewDetailPage } from './pages/provider/CandidateReviewDetailPage';
import { ProviderPackagesPage } from './pages/provider/ProviderPackagesPage';
import { ProviderPackageDetailPage } from './pages/provider/ProviderPackageDetailPage';
import { OriginatorDigitizePage } from './pages/originator/OriginatorDigitizePage';
import { DigitizeViewPage } from './pages/provider/DigitizeViewPage';
import { CustodyPackagingPage } from './pages/provider/CustodyPackagingPage';
import { ProviderExceptionsPage } from './pages/provider/ProviderExceptionsPage';
import { ProviderExceptionDetailPage } from './pages/provider/ProviderExceptionDetailPage';
import { ProviderSearchAskPage } from './pages/provider/ProviderSearchAskPage';
import { ProviderReportsHubPage } from './pages/provider/ProviderReportsHubPage';
import { ProgramsRulesPage } from './pages/provider/ProgramsRulesPage';
import { IntegrationsWebhooksPage } from './pages/provider/IntegrationsWebhooksPage';
import { AuditSettingsPage } from './pages/provider/AuditSettingsPage';
import { CustodianDashboardPage } from './pages/custodian/CustodianDashboardPage';
import { InvestorOverviewPage } from './pages/investor/InvestorOverviewPage';
import { InvestorPackagesPage } from './pages/investor/InvestorPackagesPage';
import { InvestorPackageDetailPage } from './pages/investor/InvestorPackageDetailPage';
import { InvestorActivityPage } from './pages/investor/InvestorActivityPage';
import { InvestorAuditPage } from './pages/investor/InvestorAuditPage';
import { InvestorOrganizationsPage } from './pages/investor/InvestorOrganizationsPage';
import { InvestorProgramsPage } from './pages/investor/InvestorProgramsPage';
import { AwaitingReviewPage } from './pages/custodian/AwaitingReviewPage';
import { CustodyExplorerPage } from './pages/custodian/CustodyExplorerPage';
import { CustodianAssetRegisterPage } from './pages/custodian/CustodianAssetRegisterPage';
import { AssetCertificationDetailPage } from './pages/custodian/AssetCertificationDetailPage';
import { PledgeQueuePage } from './pages/custodian/PledgeQueuePage';
import { ReleaseCopyQueuePage } from './pages/custodian/ReleaseCopyQueuePage';
import { TransferQueuePage } from './pages/custodian/TransferQueuePage';
import { CustodianExceptionsPage } from './pages/custodian/CustodianExceptionsPage';
import { CustodianExceptionDetailPage } from './pages/custodian/CustodianExceptionDetailPage';
import { EvidenceCertificatesHubPage } from './pages/custodian/EvidenceCertificatesHubPage';
import { CustodianReceiptsPage } from './pages/custodian/CustodianReceiptsPage';
import { CustodianCertifiedCopiesPage } from './pages/custodian/CustodianCertifiedCopiesPage';
import { CustodianEvidenceBundlesPage } from './pages/custodian/CustodianEvidenceBundlesPage';
import { CustodianCertificateHistoryPage } from './pages/custodian/CustodianCertificateHistoryPage';
import { CustodianReportsHubPage } from './pages/custodian/CustodianReportsHubPage';
import { CustodianSearchAskPage } from './pages/custodian/CustodianSearchAskPage';
import { CustodianSettingsPage } from './pages/custodian/CustodianSettingsPage';

function SignInEntry() {
  const { session, ready, jwtMode } = useDemoSession();
  if (jwtMode && ready === false) {
    return <p className="p-8 text-sm text-eorig-muted">Loading…</p>;
  }
  if (session) {
    return <Navigate to={workspaceHomePath(session.workspaceId)} replace />;
  }
  // jwt-only → email form; demo or dual → demo cards (dual also links to /signin/email)
  if (isJwtOnly()) {
    return <SignInPage />;
  }
  if (isDemoAuth()) {
    return <DemoSignInPage />;
  }
  return <SignInPage />;
}

function RequireDemoSession() {
  const { session, ready, jwtMode } = useDemoSession();
  if (jwtMode && ready === false) {
    return <p className="p-8 text-sm text-eorig-muted">Loading…</p>;
  }
  if (!session) {
    return <Navigate to="/signin" replace />;
  }
  return <Outlet />;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route path="/signin" element={<SignInEntry />} />
      <Route path="/signin/email" element={<SignInPage />} />
      <Route path="/auth/callback" element={<EntraCallbackPage />} />
      <Route path="/select-workspace" element={<WorkspacePickerPage />} />
      <Route path="/invite" element={<AcceptInvitePage />} />
      <Route path="/invite/:token" element={<AcceptInvitePage />} />
      <Route element={<RequireDemoSession />}>
        <Route element={<AppLayout />}>
          <Route index element={<DashboardPage />} />
          <Route path="submission/new" element={<NewSubmissionPage />} />
          <Route path="packages" element={<PackagesPage />} />
          <Route path="packages/:packageId" element={<PackageDetailPage />} />
          <Route
            path="packages/:packageId/assets/:assetId"
            element={<OriginatorDigitizePage />}
          />
          <Route path="exceptions" element={<ExceptionsPage />} />
          <Route path="exceptions/:exceptionId" element={<ExceptionDetailPage />} />
          <Route path="loans" element={<PortfolioExplorerPage />} />
          <Route path="loans/:loanId" element={<LoanDetailPage />} />
          <Route path="requests" element={<RequestsPage />} />
          <Route path="search" element={<SearchAskPage />} />
          <Route path="reports" element={<ReportsHubPage />} />
          <Route path="reports/boarding" element={<OriginatorReportDashboardPage />} />
          <Route path="reports/authority" element={<OriginatorReportDashboardPage />} />
          <Route path="reports/custody" element={<OriginatorReportDashboardPage />} />
          <Route path="reports/spv-servicing" element={<OriginatorReportDashboardPage />} />
          <Route path="reports/evidence" element={<OriginatorReportDashboardPage />} />
          <Route path="settings/members" element={<OrgMembersPage />} />
          <Route path="settings/invitations" element={<OrgInvitationsPage />} />
          <Route path="admin/organizations" element={<AdminOrganizationsPage />} />
          <Route path="admin/connections" element={<AdminConnectionsPage />} />

          {/* Document custodian workspace */}
          <Route path="custodian" element={<CustodianDashboardPage />} />
          <Route
            path="custodian/awaiting-review"
            element={<AwaitingReviewPage />}
          />
          <Route path="custodian/explorer" element={<CustodyExplorerPage />} />
          <Route
            path="custodian/assets"
            element={<CustodianAssetRegisterPage />}
          />
          <Route
            path="custodian/assets/:assetId"
            element={<AssetCertificationDetailPage />}
          />
          <Route path="custodian/pledges" element={<PledgeQueuePage />} />
          <Route
            path="custodian/release-copy"
            element={<ReleaseCopyQueuePage />}
          />
          <Route path="custodian/transfers" element={<TransferQueuePage />} />
          <Route
            path="custodian/exceptions"
            element={<CustodianExceptionsPage />}
          />
          <Route
            path="custodian/exceptions/:exceptionId"
            element={<CustodianExceptionDetailPage />}
          />
          <Route
            path="custodian/evidence"
            element={<EvidenceCertificatesHubPage />}
          />
          <Route
            path="custodian/evidence/receipts"
            element={<CustodianReceiptsPage />}
          />
          <Route
            path="custodian/evidence/certified-copies"
            element={<CustodianCertifiedCopiesPage />}
          />
          <Route
            path="custodian/evidence/bundles"
            element={<CustodianEvidenceBundlesPage />}
          />
          <Route
            path="custodian/evidence/certificate-history"
            element={<CustodianCertificateHistoryPage />}
          />
          <Route path="custodian/search" element={<CustodianSearchAskPage />} />
          <Route
            path="custodian/reports"
            element={<CustodianReportsHubPage />}
          />
          <Route
            path="custodian/reports/inventory"
            element={<ReportDashboardPage />}
          />
          <Route
            path="custodian/reports/movement"
            element={<ReportDashboardPage />}
          />
          <Route
            path="custodian/reports/certification-receipts"
            element={<ReportDashboardPage />}
          />
          <Route
            path="custodian/reports/turnaround-sla"
            element={<ReportDashboardPage />}
          />
          <Route
            path="custodian/settings"
            element={<CustodianSettingsPage />}
          />
          <Route path="custodian/settings/members" element={<OrgMembersPage />} />
          <Route path="custodian/settings/invitations" element={<OrgInvitationsPage />} />
          <Route path="custodian/admin/organizations" element={<AdminOrganizationsPage />} />
          <Route path="custodian/admin/connections" element={<AdminConnectionsPage />} />

          {/* Provider workspace */}
          <Route path="provider" element={<ProviderDashboardPage />} />
          <Route path="provider/intake" element={<IntakeExplorerBatchPage />} />
          <Route
            path="provider/intake/programs"
            element={<IntakeExplorerProgramsPage />}
          />
          <Route
            path="provider/authority-review"
            element={<AuthorityReviewQueuePage />}
          />
          <Route
            path="provider/authority-review/:candidateId"
            element={<CandidateReviewDetailPage />}
          />
          <Route path="provider/packages" element={<ProviderPackagesPage />} />
          <Route
            path="provider/packages/:packageId"
            element={<ProviderPackageDetailPage />}
          />
          <Route path="provider/assets" element={<DigitizeViewPage />} />
          <Route
            path="provider/assets/:assetId"
            element={<DigitizeViewPage />}
          />
          <Route
            path="provider/custody-packaging"
            element={<CustodyPackagingPage />}
          />
          <Route
            path="provider/exceptions"
            element={<ProviderExceptionsPage />}
          />
          <Route
            path="provider/exceptions/:exceptionId"
            element={<ProviderExceptionDetailPage />}
          />
          <Route path="provider/search" element={<ProviderSearchAskPage />} />
          <Route path="provider/reports" element={<ProviderReportsHubPage />} />
          <Route
            path="provider/reports/intake-throughput"
            element={<ReportDashboardPage />}
          />
          <Route
            path="provider/reports/authority-outcomes"
            element={<ReportDashboardPage />}
          />
          <Route
            path="provider/reports/custody-handoff"
            element={<ReportDashboardPage />}
          />
          <Route
            path="provider/reports/integration-health"
            element={<ReportDashboardPage />}
          />
          <Route
            path="provider/programs-rules"
            element={<ProgramsRulesPage />}
          />
          <Route
            path="provider/integrations"
            element={<IntegrationsWebhooksPage />}
          />
          <Route
            path="provider/audit-settings"
            element={<AuditSettingsPage />}
          />
          <Route path="provider/settings/members" element={<OrgMembersPage />} />
          <Route path="provider/settings/invitations" element={<OrgInvitationsPage />} />
          {FEATURES.providerAdminConsole ? (
            <>
              <Route path="provider/admin/organizations" element={<OrganizationsPage />} />
              <Route path="provider/admin/organizations/new" element={<OrganizationCreatePage />} />
              <Route path="provider/admin/organizations/:orgId" element={<OrganizationDetailPage />} />
              <Route path="provider/admin/programs" element={<ProgramsPage />} />
              <Route path="provider/admin/programs/new" element={<ProgramCreatePage />} />
              <Route path="provider/admin/connections" element={<ConnectionsPage />} />
              <Route path="provider/admin/people" element={<PeoplePage />} />
              <Route path="provider/admin/roles" element={<RolesPage />} />
              <Route path="provider/admin/digitize" element={<DigitizeBindingsPage />} />
              <Route path="provider/admin/audit" element={<AdminAuditPage />} />
            </>
          ) : (
            <>
              <Route path="provider/admin/organizations" element={<AdminOrganizationsPage />} />
              <Route path="provider/admin/connections" element={<AdminConnectionsPage />} />
            </>
          )}

          <Route path="investor" element={<InvestorOverviewPage />} />
          <Route path="investor/packages" element={<InvestorPackagesPage />} />
          <Route path="investor/packages/:packageId" element={<InvestorPackageDetailPage />} />
          <Route path="investor/activity" element={<InvestorActivityPage />} />
          <Route path="investor/audit" element={<InvestorAuditPage />} />
          <Route path="investor/organizations" element={<InvestorOrganizationsPage />} />
          <Route path="investor/programs" element={<InvestorProgramsPage />} />
        </Route>
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <DemoSessionProvider>
        <AppRoutes />
      </DemoSessionProvider>
    </BrowserRouter>
  );
}

export default App;
